import type { LocaleTag, MessageNamespace } from "./locales";
import { FALLBACK_LOCALE } from "./locales";
import { resolveLocaleTag } from "./resolve";

import enCommon from "../locales/en-US/common.json";
import frCommon from "../locales/fr/common.json";
import deCommon from "../locales/de/common.json";
import itCommon from "../locales/it/common.json";
import esCommon from "../locales/es/common.json";
import ruCommon from "../locales/ru/common.json";

export type MessageKey = keyof typeof enCommon;

type NamespaceCatalog = Record<string, string>;

const catalogs: Record<LocaleTag, Record<MessageNamespace, NamespaceCatalog>> = {
  "en-US": { common: enCommon },
  fr: { common: frCommon },
  de: { common: deCommon },
  it: { common: itCommon },
  es: { common: esCommon },
  ru: { common: ruCommon },
};

export function translate(
  key: MessageKey,
  locale: string | null | undefined,
  namespace: MessageNamespace = "common",
): string {
  const tag = resolveLocaleTag(locale);
  const primary = catalogs[tag]?.[namespace]?.[key];
  if (primary) {
    return primary;
  }
  const fallback = catalogs[FALLBACK_LOCALE]?.[namespace]?.[key];
  return fallback ?? key;
}

/** Short alias matching future i18next `t()` usage. */
export function t(
  key: MessageKey,
  locale: string | null | undefined,
  namespace: MessageNamespace = "common",
): string {
  return translate(key, locale, namespace);
}

export function listMessageKeys(namespace: MessageNamespace = "common"): MessageKey[] {
  return Object.keys(catalogs[FALLBACK_LOCALE][namespace]) as MessageKey[];
}

/**
 * Whether a locale actually carries a key, with no fallback.
 *
 * `translate` answers from en-US when a locale is missing a key, so a key present in
 * English and absent everywhere else renders happily and silently in English. That
 * is worth being able to assert against when a string has just been added to the
 * catalogues by hand.
 */
export function hasMessage(
  key: MessageKey,
  locale: string | null | undefined,
  namespace: MessageNamespace = "common",
): boolean {
  const tag = resolveLocaleTag(locale);
  const value = catalogs[tag]?.[namespace]?.[key];
  return typeof value === "string" && value.length > 0;
}
